import {
  add,
  eachDayOfInterval,
  endOfISOWeek,
  endOfMonth,
  endOfWeek,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';

import { calcCurrentDate } from './calcCurrentDate';
import {
  ComparativeSection,
  makeComparisonDateFromSelectedComparativeSection,
} from './comparativeSectionUtil';

// 店舗レポート・チェーン店レポートで使用する期間タイプ
export const reportsDateRanges = [
  '前日',
  '当日',
  '前週',
  '今週',
  '前月',
  '今月',
  'カスタム',
] as const;
export type ReportsDateRange = (typeof reportsDateRanges)[number];

// 日付の単位
export const reportsDateUnits = [
  '日',
  '週',
  '月',
  '範囲選択',
  'カスタム', //カスタムから範囲選択に名称変更したが、過去のお気に入りを考慮して残す
  '自由選択',
] as const;
export type ReportsDateUnit = (typeof reportsDateUnits)[number];

/**
 * 店舗レポート・チェーン店レポート
 * 検索パラメータに渡す日付データを算出する
 *
 * @param dateRange 期間指定
 * @param dateUnit 日付タイプ
 * @param currentDate 基準日（未指定の場合は本日）
 *
 * TODO:
 * 多くの箇所で利用されているため、comparisonSectionが未指定の場合の処理を残していますが、
 * すべてのページで比較区分が実装された場合、この関数にあるymdComparisonListの期間計算を
 * 削除する必要があります。
 */
export const makeDateFromDateUnitAndRange = (
  dateRange: ReportsDateRange,
  dateUnit: ReportsDateUnit,
  currentDate: Date = new Date(),
  currentComparisonDate: Date | undefined = undefined,
  comparisonSection?: ComparativeSection,
  excludeToday?: boolean
): {
  ymdList: Date[];
  ymdComparisonList: Date[];
} => {
  let startDate = currentDate;
  let endDate = currentDate;
  let ymdList = [currentDate];
  switch (dateRange) {
    case '前日': {
      endDate = add(currentDate, { days: -1 });
      startDate = endDate;
      ymdList = [startDate];
      break;
    }
    case '当日': {
      break;
    }
    case '前週': {
      const lastWeek = add(currentDate, { days: -7 });
      startDate = startOfISOWeek(lastWeek);
      endDate = add(startDate, { days: 6 });
      ymdList = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });
      break;
    }
    case '今週': {
      startDate = startOfISOWeek(currentDate);
      ymdList = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });
      break;
    }
    case '前月': {
      const lastMonth = add(currentDate, { months: -1 });
      startDate = startOfMonth(lastMonth);
      endDate = endOfMonth(lastMonth);
      ymdList = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });
      break;
    }
    case '今月': {
      startDate = startOfMonth(currentDate);
      ymdList = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });
      break;
    }
    case 'カスタム':
      // do nothing
      break;
  }
  let startComparisonDate = startDate;
  let endComparisonDate = endDate;
  let ymdComparisonList = eachDayOfInterval({
    start: startComparisonDate,
    end: endComparisonDate,
  });
  switch (dateUnit) {
    case '日': {
      startDate = endDate;
      ymdList = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });
      if (currentComparisonDate) {
        startComparisonDate = endComparisonDate = currentComparisonDate;
        ymdComparisonList = eachDayOfInterval({
          start: startComparisonDate,
          end: endComparisonDate,
        });
      } else {
        endComparisonDate = add(startDate, { days: -7 });
        startComparisonDate = endComparisonDate;
        ymdComparisonList = eachDayOfInterval({
          start: startComparisonDate,
          end: endComparisonDate,
        });
      }
      break;
    }
    case '週': {
      startDate = startOfISOWeek(endDate);
      endDate = endOfISOWeek(startDate);
      ymdList = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });
      if (currentComparisonDate) {
        startComparisonDate = startOfISOWeek(currentComparisonDate);
        endComparisonDate = endOfISOWeek(currentComparisonDate);
        ymdComparisonList = eachDayOfInterval({
          start: startComparisonDate,
          end: endComparisonDate,
        });
      } else {
        const lastWeek = add(startDate, { days: -7 });
        startComparisonDate = startOfISOWeek(lastWeek);
        endComparisonDate = endOfISOWeek(lastWeek);
        ymdComparisonList = eachDayOfInterval({
          start: startComparisonDate,
          end: endComparisonDate,
        });
      }
      break;
    }
    case '月': {
      startDate = startOfMonth(endDate);
      endDate = endOfMonth(startDate);
      ymdList = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });
      if (currentComparisonDate) {
        startComparisonDate = startOfMonth(currentComparisonDate);
        endComparisonDate = endOfMonth(currentComparisonDate);
        ymdComparisonList = eachDayOfInterval({
          start: startComparisonDate,
          end: endComparisonDate,
        });
      } else {
        const lastMonth = add(startDate, { months: -1 });
        startComparisonDate = startOfMonth(lastMonth);
        endComparisonDate = endOfMonth(lastMonth);
        ymdComparisonList = eachDayOfInterval({
          start: startComparisonDate,
          end: endComparisonDate,
        });
      }
      break;
    }
    case '自由選択': {
      break;
    }
  }
  // 最終日が本日より大きい場合は本日に合わせる
  const today = new Date();
  ymdList = eachDayOfInterval({
    start: startDate,
    end: endDate > today ? today : endDate,
  });

  // comparisonSectionとexcludeTodayが指定されている場合は
  // ymdComparisonListを再計算する
  if (
    comparisonSection &&
    comparisonSection !== 'カスタム' &&
    comparisonSection !== '平日/土日祝比較' &&
    excludeToday != null
  ) {
    const currentDate = calcCurrentDate(dateRange);
    const { ymdComparisonList } =
      makeComparisonDateFromSelectedComparativeSection(
        comparisonSection,
        currentDate,
        new Date(),
        excludeToday
      );
    return {
      ymdList,
      ymdComparisonList,
    };
  }

  return {
    ymdList,
    ymdComparisonList,
  };
};

/**
 * 店舗レポート・チェーン店レポート
 * 基準日と選択日と日付単位から指定された日付の末尾を取得する
 * @param dateUnit 日付単位
 * @param selectDate 選択された日付
 * @param currentDate 基準日（未指定の場合は本日）
 */
export const makeSelectedDateFromDateUnit = (
  dateUnit: ReportsDateUnit,
  selectDate: Date,
  currentDate: Date = new Date()
): Date => {
  switch (dateUnit) {
    case '日': {
      // 日単位の場合は選択された日付をそのまま返す
      if (selectDate > currentDate) {
        return currentDate;
      } else {
        return selectDate;
      }
    }
    case '週': {
      // 週単位で基準日を超えていなければ選択された週の末尾を返す
      const selectedDate = endOfWeek(selectDate, { weekStartsOn: 1 });
      if (selectedDate > currentDate) {
        return currentDate;
      } else {
        return selectedDate;
      }
    }
    case '月': {
      // 月単位で基準日を超えていなければ選択された月の末尾を返す
      const selectedDate = endOfMonth(selectDate);
      if (selectedDate > currentDate) {
        return currentDate;
      } else {
        return selectedDate;
      }
    }
    case 'カスタム':
    case '範囲選択': {
      // 範囲選択（カスタム）の場合は選択日が末尾になるが、最終日が本日より大きい場合は本日に合わせる
      if (selectDate > currentDate) {
        return currentDate;
      }
      return selectDate;
    }
    case '自由選択': {
      // 自由選択の場合は選択日が末尾になるが、最終日が本日より大きい場合は本日に合わせる
      if (selectDate > currentDate) {
        return currentDate;
      }
      return selectDate;
    }
  }
};

/**
 * 店舗レポート・チェーン店レポート
 * 期間指定から対象の日付単位を取得する
 * @param dateRange 期間指定
 */
export const makeDateUnit = (dateRange: ReportsDateRange): ReportsDateUnit => {
  switch (dateRange) {
    case '前日':
      return '日';
    case '当日':
      return '日';
    case '前週':
      return '週';
    case '今週':
      return '週';
    case '前月':
      return '月';
    case '今月':
      return '月';
    case 'カスタム':
      return '範囲選択';
    default:
      // もしくは該当しない単位の場合は範囲選択を返す
      return '範囲選択';
  }
};

/**
 * 店舗レポート・チェーン店レポート
 * 日付タイプの名称変換（過去のお気に入りの呼び出し時に使用）
 * @param dateUnit 日付タイプ
 */
export const changeNameDateUnit = (
  dateUnit: ReportsDateUnit
): ReportsDateUnit => {
  switch (dateUnit) {
    case 'カスタム':
      return '範囲選択';
    default:
      return dateUnit;
  }
};
